import { FontPicker } from "./FontPicker";
import {
  type Typography,
  type TypographyOverrides,
  type FontFaceDescriptor,
} from "@proof/core";
import { Select, Control } from "./components";
export const typographyLabel = (key: string) =>
  ({
    fontFamily: "font family",
    fontFaceId: "managed font",
    fontSize: "font size",
    fontWeight: "weight",
    color: "color",
    lineHeight: "line height",
    align: "alignment",
  })[key] || key;
export function TypographyControls({
  value,
  fonts,
  onChange,
  onFont,
  prefix = "",
}: {
  value: Typography;
  fonts: Map<string, FontFaceDescriptor>;
  onChange: (patch: TypographyOverrides) => void;
  onFont: (face: FontFaceDescriptor | null) => void;
  prefix?: string;
}) {
  const label = (s: string) => (prefix ? `${prefix} ${s.toLowerCase()}` : s);
  return (
    <>
      <Control
        label={label("Managed font face")}
        note="Exact font bytes and variants stay pinned to this document."
      >
        <FontPicker
          fonts={fonts}
          label={label("Managed font face")}
          value={value.fontFaceId || undefined}
          onChange={onFont}
        />
      </Control>
      <div className="control-grid">
        <Control label={label("Font family")}>
          <Select
            value={value.fontFamily}
            disabled={!!value.fontFaceId}
            onChange={(e) => onChange({ fontFamily: e.target.value })}
          >
            {[
              ...new Set([
                "Georgia",
                "Inter",
                "Libre Baskerville",
                value.fontFamily,
              ]),
            ]
              .sort((a, b) => a.localeCompare(b, "en", { sensitivity: "base" }))
              .map((family) => (
                <option key={family} style={{ fontFamily: family }}>
                  {family}
                </option>
              ))}
          </Select>
        </Control>
        <Control label={label("Font size")}>
          <input
            type="number"
            min={1}
            max={14400}
            value={value.fontSize}
            onChange={(e) => onChange({ fontSize: Number(e.target.value) })}
          />
        </Control>
        <Control label={label("Weight")}>
          <Select
            value={value.fontWeight}
            disabled={!!value.fontFaceId}
            onChange={(e) => onChange({ fontWeight: Number(e.target.value) })}
          >
            {![400, 700].includes(value.fontWeight) && (
              <option value={value.fontWeight}>{value.fontWeight}</option>
            )}
            <option value="400">Regular</option>
            <option value="700">Bold</option>
          </Select>
        </Control>
        <Control label={label("Color")}>
          <input
            type="color"
            value={value.color}
            onChange={(e) => onChange({ color: e.target.value })}
          />
        </Control>
        <Control label={label("Alignment")}>
          <Select
            value={value.align}
            onChange={(e) =>
              onChange({ align: e.target.value as Typography["align"] })
            }
          >
            {(["left", "center", "right"] as const).map((a) => (
              <option key={a}>{a}</option>
            ))}
          </Select>
        </Control>
        <Control label={label("Line height")}>
          <input
            type="number"
            min={0.1}
            max={100}
            step={0.05}
            value={value.lineHeight}
            onChange={(e) => onChange({ lineHeight: Number(e.target.value) })}
          />
        </Control>
      </div>
    </>
  );
}
